/**
 * Read API – collated data from Cloud Functions (feed, schedule, tournament, dashboard).
 * Caching: schedule, dashboard, tournament list, feed use local cache; return cache first, refresh in background.
 * Pass { skipCache: true } to force a fresh fetch. In-flight deduplication for identical concurrent calls.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase/config';
import { getActAsUserId } from '../impersonationStore';
import * as cached from './cachedReadApi';

const functions = getFunctions(app, 'us-central1');

function call(name, data = {}) {
  const fn = httpsCallable(functions, name);
  const actAs = getActAsUserId();
  const payload = actAs ? { ...data, actAsUserId: actAs } : data;
  return fn(payload).then((res) => res.data); // payload includes actAsUserId when impersonating
}

/**
 * Feed updates: cache-first, then refresh in background.
 */
export async function getFeedUpdates(gameId = 'table-tennis', options = {}) {
  return cached.getFeedUpdates(gameId, options);
}

/**
 * Schedule data: cache-first, then refresh in background (delta-style: backend returns asOf).
 */
export async function getScheduleData(gameId = 'table-tennis', options = {}) {
  return cached.getScheduleData(gameId, options);
}

/**
 * Tournament list: cache-first, then refresh in background.
 */
export async function getTournamentList(gameId = 'table-tennis', options = {}) {
  return cached.getTournamentList(gameId, options);
}

/**
 * Dashboard data: cache-first, then refresh in background.
 */
export async function getDashboardData(gameId = 'table-tennis', options = {}) {
  return cached.getDashboardData(gameId, options);
}

/** Invalidate cached data so next fetch is fresh (e.g. after mutation or conflict resolve). */
export { invalidateCache, invalidateStatisticsCache, invalidatePlayerProfileCache, CACHE_KEYS } from '../services/dataCache';

/** Patch individual match in schedule/dashboard cache after mutation (avoids full refetch). */
export { patchScheduleMatch, patchDashboardMatch } from './cachedReadApi';

/**
 * My profile for AuthContext – single source from backend (no direct Firestore).
 */
export async function getMyProfile() {
  const res = await call('getMyProfile', {});
  return res?.profile ?? null;
}

/**
 * App config (enabled games, public env). No direct Firestore.
 */
export async function getConfig() {
  return call('getConfig', {});
}

/**
 * Schedule slot info for one match (tournament + other scheduled). For reschedule flow.
 */
export async function getScheduleSlotInfo(matchId) {
  return call('getScheduleSlotInfo', { matchId });
}

/** Open data – no auth. Batched landing data: enabled games, open tournaments, recent scores per game. Cached 5 min. */
export async function getLandingData(options = {}) {
  return cached.getLandingData(options);
}

/** Open data – no auth. List of open tournaments for landing / discovery. */
export async function getOpenTournaments(gameId = 'table-tennis') {
  return call('getOpenTournaments', { gameId });
}

/** Open data – no auth. Public details for one tournament (for deep links before sign-in). */
export async function getTournamentPublicDetails(tournamentId) {
  return call('getTournamentPublicDetails', { tournamentId });
}

/** Tournament + existing registrations for current user. Auth required. */
export async function getTournamentRegistrationData(tournamentId, gameId = 'table-tennis') {
  return call('getTournamentRegistrationData', { tournamentId, gameId });
}

/**
 * Statistics rankings only. Cache-first, lazy-load friendly. Auth required.
 */
export async function getStatisticsRankings(gameId = 'table-tennis', options = {}) {
  return cached.getStatisticsRankings(gameId, options);
}

/**
 * Statistics recent matches only. Cache-first, lazy-load friendly. Auth required.
 */
export async function getStatisticsRecentMatches(gameId = 'table-tennis', options = {}) {
  return cached.getStatisticsRecentMatches(gameId, options);
}

/**
 * Statistics full data (rankingsSingles, rankingsDoubles, recentMatches). Leaderboard is tournament-scoped when tournamentId provided.
 * Auth required. All reads via Cloud Function; no direct Firestore.
 */
export async function getStatisticsData(gameId = 'table-tennis', options = {}) {
  return call('getStatisticsData', { gameId, part: options.part, tournamentId: options.tournamentId || undefined });
}

/**
 * Player profile + stats for a user (profile page, public player page, profile stats modal).
 * Cached 30 days in localStorage; pass { skipCache: true } to force refresh.
 */
export async function getPlayerProfileData(targetUserId, options = {}) {
  return cached.getPlayerProfileData(targetUserId, options);
}

/** Register for tournament (singles or doubles). Auth required. */
export async function registerForTournament(tournamentId, type, playerEmail) {
  return call('registerForTournament', { tournamentId, type, playerEmail });
}

/**
 * Match data for scoring page (polling). Auth required. Returns match + venueDisplay + tournamentConfig.
 */
export async function getMatchForScoring(matchId) {
  return call('getMatchForScoring', { matchId });
}

/**
 * Public match – no auth. Single match for public permalinks.
 */
export async function getPublicMatch(matchId) {
  return call('getPublicMatch', { matchId });
}

/**
 * Public matches – no auth. List for public pages. tournamentId optional.
 */
export async function getPublicMatches(gameId = 'table-tennis', tournamentId = null, limit = 50) {
  return call('getPublicMatches', { gameId, tournamentId: tournamentId || undefined, limit });
}

/**
 * Get display info for users by IDs. For PlayerCache.
 */
export async function getUsersByIds(userIds) {
  const res = await call('getUsersByIds', { userIds });
  return res?.users ?? {};
}

/**
 * Get playerStats for a user. Pass targetUid (use effective UID when viewing own stats).
 */
export async function getPlayerStats(targetUid) {
  const res = await call('getPlayerStats', { targetUid });
  return res?.stats ?? null;
}

/**
 * Get nav data (apartments, groups, userGroups, isApartmentAdmin, isGroupAdmin) for effective user.
 */
export async function getNavData() {
  return call('getNavData', {});
}
